import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  Package,
  Loader2,
  RefreshCw,
  AlertCircle,
  Filter,
  BarChart3,
  Calendar,
  ChevronDown,
  ChevronUp,
  Users,
  Ban
} from 'lucide-react';
import { odooAPI, formatApiError } from '../services/api';
import CoversByDayChart from '../components/sales/CoversByDayChart';
import AvgTicketByDayChart from '../components/sales/AvgTicketByDayChart';
import WeeklyEmailPanel from '../components/reports/WeeklyEmailPanel';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';

const CATEGORY_COLORS = [
  { bg: 'bg-blue-500/10', text: 'text-blue-600', border: 'border-blue-500/30', dot: 'bg-blue-500' },
  { bg: 'bg-red-500/10', text: 'text-red-600', border: 'border-red-500/30', dot: 'bg-red-500' },
  { bg: 'bg-emerald-500/10', text: 'text-emerald-600', border: 'border-emerald-500/30', dot: 'bg-emerald-500' },
  { bg: 'bg-neon-pink/10', text: 'text-neon-pink', border: 'border-neon-pink/30', dot: 'bg-neon-pink' },
  { bg: 'bg-orange-500/10', text: 'text-orange-600', border: 'border-orange-500/30', dot: 'bg-orange-500' },
  { bg: 'bg-purple-500/10', text: 'text-purple-600', border: 'border-purple-500/30', dot: 'bg-purple-500' },
  { bg: 'bg-cyan-500/10', text: 'text-cyan-600', border: 'border-cyan-500/30', dot: 'bg-cyan-500' },
  { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300', dot: 'bg-neon-yellow' },
  { bg: 'bg-rose-500/10', text: 'text-rose-600', border: 'border-rose-500/30', dot: 'bg-rose-500' },
  { bg: 'bg-gray-100', text: 'text-gray-500', border: 'border-gray-200', dot: 'bg-gray-400' },
];

const SalesPage = () => {
  const [salesData, setSalesData] = useState([]);
  const [posCategories, setPosCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [error, setError] = useState(null);

  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [service, setService] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [periodInfo, setPeriodInfo] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [posStats, setPosStats] = useState(null);
  const [cancelled, setCancelled] = useState(null);
  const [showCancelled, setShowCancelled] = useState(false);

  // Fetch POS categories from Odoo
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const res = await odooAPI.getPOSCategories();
        const raw = res.data.data || [];
        // Tri personnalisé des catégories POS
        const ORDER = [
          'salades', 'cold mezzes', 'hot mezzes', 'main courses', 'desserts',
          'menus', 'boissons', 'cave', 'kamakian epicerie'
        ];
        const sorted = [...raw]
          .filter(c => c.complete_name !== 'ALL / POS') // exclure le parent
          .sort((a, b) => {
            const aName = a.name.toLowerCase();
            const bName = b.name.toLowerCase();
            const aIdx = ORDER.findIndex(o => aName.includes(o));
            const bIdx = ORDER.findIndex(o => bName.includes(o));
            const aOrder = aIdx === -1 ? ORDER.length : aIdx;
            const bOrder = bIdx === -1 ? ORDER.length : bIdx;
            return aOrder - bOrder;
          });
        setPosCategories(sorted);
      } catch (err) {
        console.error('Failed to fetch POS categories:', err);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = { date_from: dateFrom, date_to: dateTo };
      if (service !== 'all') params.service = service;
      if (categoryFilter !== 'all') {
        params.category_id = parseInt(categoryFilter);
      }

      const [salesRes, statsRes] = await Promise.all([
        odooAPI.getSalesAnalysis(params),
        odooAPI.getPOSStats({ date_from: dateFrom, date_to: dateTo, compare: true, ...(service !== 'all' ? { service } : {}) }),
      ]);
      setSalesData(salesRes.data.data || []);
      setPeriodInfo(salesRes.data.period);
      setCancelled(salesRes.data.cancelled || null);
      setPosStats(statsRes.data.data || null);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, service, categoryFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const groupedByCategory = salesData.reduce((acc, product) => {
    const cat = product.category || 'Autres';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(product);
    return acc;
  }, {});

  // Tri des catégories dans l'ordre personnalisé
  const CATEGORY_ORDER = [
    'salades', 'cold mezzes', 'hot mezzes', 'main courses', 'desserts',
    'menus', 'boissons', 'cave', 'kamakian epicerie'
  ];
  const sortedCategories = Object.entries(groupedByCategory).sort(([a], [b]) => {
    const aName = a.toLowerCase();
    const bName = b.toLowerCase();
    const aIdx = CATEGORY_ORDER.findIndex(o => aName.includes(o));
    const bIdx = CATEGORY_ORDER.findIndex(o => bName.includes(o));
    const aOrder = aIdx === -1 ? CATEGORY_ORDER.length : aIdx;
    const bOrder = bIdx === -1 ? CATEGORY_ORDER.length : bIdx;
    return aOrder - bOrder;
  });

  const totalProducts = salesData.reduce((sum, p) => sum + p.total_qty, 0);

  const setQuickPeriod = (days) => {
    setDateFrom(format(subDays(new Date(), days), 'yyyy-MM-dd'));
    setDateTo(format(new Date(), 'yyyy-MM-dd'));
  };

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const getCategoryColor = (categoryName, index) => {
    return CATEGORY_COLORS[index % CATEGORY_COLORS.length];
  };

  return (
    <div className="min-h-screen p-3 md:p-8 pb-24 md:pb-8" data-testid="sales-page">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-8">
        <div className="flex items-center justify-between mb-6 pr-10 md:pr-0">
          <div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-gray-900 tracking-tight mb-1">
              Analyse des Ventes
            </h1>
            <p className="text-gray-500 text-sm">
              Frequences et moyennes pour anticiper la production
            </p>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={fetchData}
            className="rounded-xl bg-white border-gray-200 text-gray-700 hover:bg-gray-100"
            data-testid="refresh-sales-btn"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-2xl p-4 md:p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={18} className="text-neon-pink" />
            <h2 className="font-heading font-bold text-lg text-gray-900">Filtres</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <Label className="text-sm text-gray-500">Du</Label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="mt-1 block w-full h-10 rounded-xl bg-gray-100 border border-gray-200 text-gray-900 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-neon-pink/50"
                data-testid="date-from-input"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-500">Au</Label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="mt-1 block w-full h-10 rounded-xl bg-gray-100 border border-gray-200 text-gray-900 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-neon-pink/50"
                data-testid="date-to-input"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-500">Service</Label>
              <select value={service} onChange={(e) => setService(e.target.value)}
                className="mt-1 block w-full h-10 rounded-xl bg-gray-100 border border-gray-200 text-gray-800 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-neon-pink/50"
                data-testid="service-select">
                <option value="all">Tous les services</option>
                <option value="lunch">Midi (avant 15h)</option>
                <option value="dinner">Soir (apres 15h)</option>
              </select>
            </div>
            <div>
              <Label className="text-sm text-gray-500">Categorie POS</Label>
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                className="mt-1 block w-full h-10 rounded-xl bg-gray-100 border border-gray-200 text-gray-800 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-neon-pink/50"
                data-testid="category-select">
                <option value="all">Toutes les categories</option>
                {posCategories.map(cat => (
                  <option key={cat.id} value={String(cat.id)}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Hier', days: 1 },
              { label: '7 jours', days: 7 },
              { label: '14 jours', days: 14 },
              { label: '30 jours', days: 30 },
              { label: '90 jours', days: 90 },
              { label: '185 jours', days: 185 },
            ].map(p => (
              <Button
                key={p.days}
                variant="outline"
                size="sm"
                onClick={() => setQuickPeriod(p.days)}
                className="rounded-full bg-gray-100 border-gray-200 text-gray-700 hover:bg-neon-pink/10 hover:text-neon-pink hover:border-neon-pink/30"
                data-testid={`quick-period-${p.days}`}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
          <motion.div whileHover={{ scale: 1.02 }} className="bg-white border border-gray-200 rounded-2xl p-4 md:p-5">
            <div className="flex items-center gap-2 text-gray-500 mb-2">
              <Package size={16} />
              <span className="text-xs font-bold uppercase tracking-wider">Articles vendus</span>
            </div>
            <div className="font-heading text-2xl md:text-3xl font-bold text-gray-900">{totalProducts.toFixed(0)}</div>
          </motion.div>

          <motion.div whileHover={{ scale: 1.02 }} className="bg-white border border-neon-pink/20 rounded-2xl p-4 md:p-5">
            <div className="flex items-center gap-2 text-neon-pink mb-2">
              <TrendingUp size={16} />
              <span className="text-xs font-bold uppercase tracking-wider">Moy. /jour</span>
            </div>
            <div className="font-heading text-2xl md:text-3xl font-bold text-neon-pink neon-text-pink">
              {periodInfo?.num_days ? (totalProducts / periodInfo.num_days).toFixed(0) : '-'}
            </div>
          </motion.div>

          <motion.div whileHover={{ scale: 1.02 }} className="bg-white border border-gray-200 rounded-2xl p-4 md:p-5">
            <div className="flex items-center gap-2 text-gray-500 mb-2">
              <BarChart3 size={16} />
              <span className="text-xs font-bold uppercase tracking-wider">Produits</span>
            </div>
            <div className="font-heading text-2xl md:text-3xl font-bold text-gray-900">{salesData.length}</div>
          </motion.div>

          <motion.div whileHover={{ scale: 1.02 }} className="bg-white border border-neon-yellow/20 rounded-2xl p-4 md:p-5">
            <div className="flex items-center gap-2 text-amber-700 mb-2">
              <Calendar size={16} />
              <span className="text-xs font-bold uppercase tracking-wider">Jours</span>
            </div>
            <div className="font-heading text-2xl md:text-3xl font-bold text-amber-700 ">{periodInfo?.num_days || '-'}</div>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-neon-pink animate-spin mb-4" />
            <p className="text-gray-500">Chargement des donnees de vente...</p>
          </div>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold text-red-600 mb-2">Erreur</h3>
            <p className="text-red-300 mb-4">{error}</p>
            <Button onClick={fetchData} variant="outline" className="border-red-500/30 text-red-600 hover:bg-red-500/10">
              Reessayer
            </Button>
          </div>
        ) : salesData.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">
              Aucune donnee de vente
            </h3>
            <p className="text-gray-500">
              Modifiez les filtres ou la periode pour voir les ventes
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedCategories.map(([category, products], catIdx) => {
              const color = getCategoryColor(category, catIdx);
              const isExpanded = expandedCategories[category] === true;
              const catTotal = products.reduce((s, p) => s + p.total_qty, 0);
              const catRevenue = products.reduce((s, p) => s + (p.total_revenue || 0), 0);
              const catAvg = periodInfo?.num_days ? (catTotal / periodInfo.num_days).toFixed(1) : '-';

              return (
                <div key={category} className={`bg-white border ${color.border} rounded-2xl overflow-hidden`} data-testid={`sales-category-${category}`}>
                  {/* Category Header - Clickable dropdown */}
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full px-4 md:px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    data-testid={`sales-category-toggle-${category}`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-3 h-3 rounded-full ${color.dot} flex-shrink-0`} />
                      <div className="text-left min-w-0">
                        <h3 className="font-heading font-bold text-gray-900 text-sm md:text-base truncate">{category}</h3>
                        <span className="text-xs text-gray-400">
                          {products.length} produits &bull; {catTotal.toFixed(0)} vendus &bull; ~{catAvg}/jour &bull; {catRevenue.toFixed(0)} CHF
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <Badge className={`${color.bg} ${color.text} border ${color.border} text-xs`}>
                        {catTotal.toFixed(0)}
                      </Badge>
                      <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs">
                        {catRevenue.toFixed(0)} CHF
                      </Badge>
                      {isExpanded ? (
                        <ChevronUp className="text-gray-500" size={18} />
                      ) : (
                        <ChevronDown className="text-gray-500" size={18} />
                      )}
                    </div>
                  </button>

                  {/* Product Table - Collapsible */}
                  {isExpanded && (
                    <div className="border-t border-gray-100">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Produit</th>
                              <th className="text-center py-3 px-2 text-xs font-bold text-gray-400 uppercase tracking-wider w-24">Total</th>
                              <th className="text-center py-3 px-2 text-xs font-bold text-gray-400 uppercase tracking-wider w-24">Moy./jour</th>
                            </tr>
                          </thead>
                          <tbody>
                            {products.map((product) => (
                              <tr
                                key={product.product_id}
                                className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                                data-testid={`sales-product-${product.product_id}`}
                              >
                                <td className="py-3 px-4">
                                  <p className="font-medium text-gray-900 text-sm truncate">{product.product_name}</p>
                                </td>
                                <td className="py-3 px-2 text-center">
                                  <span className="font-heading text-lg font-bold text-gray-900">
                                    {product.total_qty.toFixed(0)}
                                  </span>
                                </td>
                                <td className="py-3 px-2 text-center">
                                  <span className="inline-flex items-center justify-center w-14 h-8 bg-neon-pink/10 border border-neon-pink/20 rounded-lg">
                                    <span className="font-heading text-sm font-bold text-neon-pink">
                                      {product.daily_average}
                                    </span>
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Convives & Ticket Moyen */}
        {!loading && !error && posStats && (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <motion.div whileHover={{ scale: 1.02 }} className="bg-white border border-purple-200 rounded-2xl p-5">
              <div className="flex items-center gap-2 text-purple-600 mb-2">
                <Users size={16} />
                <span className="text-xs font-bold uppercase tracking-wider">Convives</span>
              </div>
              <div className="font-heading text-3xl font-bold text-purple-700" data-testid="stat-convives">{posStats.total_covers}</div>
              <p className="text-gray-400 text-xs mt-1">~{posStats.avg_covers_per_day}/jour sur {posStats.num_days} jours</p>
            </motion.div>
            <motion.div whileHover={{ scale: 1.02 }} className="bg-white border border-emerald-200 rounded-2xl p-5">
              <div className="flex items-center gap-2 text-emerald-600 mb-2">
                <BarChart3 size={16} />
                <span className="text-xs font-bold uppercase tracking-wider">Ticket Moyen</span>
              </div>
              <div className="font-heading text-3xl font-bold text-emerald-700" data-testid="stat-ticket-moyen">{posStats.avg_ticket} CHF</div>
              <p className="text-gray-400 text-xs mt-1">{posStats.total_covers} convives &bull; {posStats.total_revenue.toFixed(0)} CHF total</p>
            </motion.div>
          </div>
        )}

        {!loading && !error && posStats?.by_day?.length > 0 && (
          <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-4">
            <CoversByDayChart byDay={posStats.by_day} byDayPrev={posStats.by_day_prev} service={posStats.service} />
            <AvgTicketByDayChart byDay={posStats.by_day} byDayPrev={posStats.by_day_prev} service={posStats.service} />
          </div>
        )}

        {cancelled && (
          <div className="mt-8 bg-white border border-red-200 rounded-2xl overflow-hidden" data-testid="cancelled-sales-recap">
            <button type="button" onClick={() => setShowCancelled(v => !v)} className="w-full flex flex-wrap items-center gap-2 px-4 py-3 text-left min-h-[52px]" data-testid="cancelled-sales-toggle">
              <Ban size={16} className="text-red-500" />
              <span className="text-sm font-semibold text-gray-800">Ventes annulées (non comptabilisées)</span>
              <span className="text-sm text-gray-600 ml-auto" data-testid="cancelled-sales-summary">
                {cancelled.orders} commande{cancelled.orders > 1 ? 's' : ''} &bull; {cancelled.items} article{cancelled.items > 1 ? 's' : ''} &bull; {cancelled.amount.toFixed(2)} CHF
              </span>
              {cancelled.orders > 0 && (showCancelled ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />)}
            </button>
            {showCancelled && cancelled.orders > 0 && (
              <div className="border-t border-red-100 divide-y divide-gray-100" data-testid="cancelled-sales-list">
                {cancelled.list.map(o => (
                  <div key={o.order_id} className="px-4 py-2.5 text-sm flex flex-wrap gap-x-3 gap-y-1" data-testid={`cancelled-order-${o.order_id}`}>
                    <span className="font-mono text-gray-800">{o.name}</span>
                    <span className="text-gray-500">{o.date.replace('T', ' ')}</span>
                    <span className="text-gray-700">{o.items} art.</span>
                    <span className="text-gray-900 font-medium">{o.amount.toFixed(2)} CHF</span>
                    {o.products.length > 0 && <span className="w-full text-xs text-gray-500">{o.products.join(', ')}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-6">
          <WeeklyEmailPanel kind="ventes" period={{ from: dateFrom, to: dateTo }} title="Envoi hebdomadaire de l'analyse des ventes (PDF avec graphiques + CSV des articles)" sendPeriodLabel="Envoyer la période affichée" />
        </div>
      </div>
    </div>
  );
};

export default SalesPage;
